import React from 'react';
const { useState, useEffect, useMemo } = React;
import { inputSt, textareaSt, labelSt, cardSt, mutedSt, warnSt } from '../diagnostic/shared.jsx';
import { LIFE_KINDS, LIFE_STATUSES, ASK_LIMITS } from '../../ownerSpaceConfig.js';
import { ownerCall, Notice, chipSt, LevelBadge } from './shared.jsx';

// Consultas sobre lo aprendido: questions about one material or several. The
// answer separates what the materials say, what my notes say and what the AI
// suggests; every point shows its sources (from the records, never from the
// AI) and the screen says what was consulted and what was not.

function Refs({ refs, citations }) {
  const [open, setOpen] = useState(null);
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', marginLeft: 4 }}>
      {refs.map(r => {
        const c = citations.find(x => x.ref === r);
        if (!c) return null;
        return (
          <span key={r}>
            <button style={{ ...chipSt(open === r), fontSize: 11, padding: '1px 8px' }} onClick={() => setOpen(open === r ? null : r)} title={c.snippet}>
              {c.title}{c.locator ? ` · ${c.locator}` : ''}
            </button>
            {open === r && <span style={{ display: 'block', fontSize: 12, color: 'var(--globant-muted)', borderLeft: '2px solid var(--globant-border)', padding: '4px 8px', margin: '4px 0', whiteSpace: 'pre-wrap' }}>{c.snippet}{c.snippet.length >= 280 ? '…' : ''}</span>}
          </span>
        );
      })}
    </span>
  );
}

function Answer({ r }) {
  const { answer, citations, consulted, coverage, checks } = r;
  const section = (title, list, tone) => (
    <div style={{ marginTop: 12 }}>
      <span style={labelSt}>{title}</span>
      {!list.length ? <div style={mutedSt}>—</div> : list.map((x, i) => (
        <div key={i} style={{ fontSize: 13, margin: '6px 0', borderLeft: `2px solid ${tone}`, paddingLeft: 8 }}>{x.text}{x.refs && <Refs refs={x.refs} citations={citations} />}</div>
      ))}
    </div>
  );
  return (
    <div style={cardSt}>
      {!answer.enough && (
        <div style={{ ...warnSt, padding: 10 }}>
          <b>No hay información suficiente</b> en lo consultado para responder.
          {answer.missing === 'no_content' ? ' Los materiales elegidos solo tienen sus datos (o están excluidos de la IA): agrega notas, fragmentos o contenido.'
            : answer.missing === 'no_match' ? ' Ningún fragmento disponible habla de eso. Prueba con otras palabras o elige el material.'
            : answer.missing ? ` ${answer.missing}` : ''}
        </div>
      )}
      {section('📖 Lo que dicen las fuentes', answer.fromSources, 'var(--globant-green)')}
      {section('📝 Lo que dicen tus notas', answer.fromNotes, '#2D5F99')}
      {section('✨ Sugerencias de la IA (no vienen de tus materiales)', answer.suggestions, '#BFD730')}
      {(checks.dropped > 0 || checks.unverifiedQuotes > 0 || checks.scientificClaims.length > 0) && (
        <div style={{ ...mutedSt, marginTop: 10 }}>
          Controles: {[checks.dropped > 0 && `${checks.dropped} afirmación${checks.dropped === 1 ? '' : 'es'} sin respaldo en lo consultado se quitaron`,
            checks.unverifiedQuotes > 0 && `${checks.unverifiedQuotes} cita${checks.unverifiedQuotes === 1 ? '' : 's'} que no está${checks.unverifiedQuotes === 1 ? '' : 'n'} en el texto perdieron las comillas`,
            checks.scientificClaims.length > 0 && `menciona respaldo científico que tus materiales no aportan (${checks.scientificClaims.join(', ')}): no lo tomes como dato`].filter(Boolean).join(' · ')}.
        </div>
      )}
      <div style={{ marginTop: 14, borderTop: '1px solid var(--globant-border)', paddingTop: 10, fontSize: 12 }}>
        <b>Qué se consultó.</b>{' '}
        {coverage.passagesSent
          ? <>Se enviaron a la IA {coverage.passagesSent} fragmento{coverage.passagesSent === 1 ? '' : 's'} de {consulted.length} material{consulted.length === 1 ? '' : 'es'}{coverage.everythingInScope ? ': todo lo disponible de lo elegido.' : `, los más relacionados con tu pregunta, de ${coverage.passagesAvailable} disponibles. No es una revisión completa de ${coverage.itemsInScope === 1 ? 'ese material' : 'la biblioteca'}.`}</>
          : <>No se envió nada a la IA.</>}
        {consulted.map(c => <div key={c.itemId} style={{ ...mutedSt, marginTop: 4 }}>· {c.title}{c.creator ? ` — ${c.creator}` : ''}: {c.fromSource} de la fuente, {c.fromNotes} de tus notas{c.locators.length ? ` (${[...new Set(c.locators)].slice(0, 8).join(', ')})` : ''}</div>)}
        {coverage.itemsWithoutText.length > 0 && <div style={{ ...mutedSt, marginTop: 4 }}>Sin texto para consultar (solo referencia{coverage.itemsWithoutText.some(i => i.contentStatus === 'needs_ocr') ? ' o PDF sin texto' : ''}): {coverage.itemsWithoutText.slice(0, 10).map(i => i.title).join(', ')}{coverage.itemsWithoutText.length > 10 ? '…' : ''}</div>}
        {(coverage.itemsExcluded > 0 || coverage.fragmentsExcluded > 0) && <div style={{ ...mutedSt, marginTop: 4 }}>Excluido por ti de la IA: {coverage.itemsExcluded} material{coverage.itemsExcluded === 1 ? '' : 'es'} y {coverage.fragmentsExcluded} fragmento{coverage.fragmentsExcluded === 1 ? '' : 's'}.</div>}
      </div>
    </div>
  );
}

export default function AskPanel({ initialItemId }) {
  const [items, setItems] = useState(null);
  const [mode, setMode] = useState(initialItemId ? 'one' : 'all');
  const [one, setOne] = useState(initialItemId || '');
  const [several, setSeveral] = useState([]);
  const [filters, setFilters] = useState({ kind: '', status: '', topic: '' });
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  useEffect(() => { ownerCall({ action: 'listLibrary' }).then(d => setItems(d.items)).catch(e => setError(e.message)); }, []);
  const topics = useMemo(() => [...new Set((items || []).flatMap(i => [...i.topics, ...i.tags]))].sort(), [items]);
  const consultable = (items || []).filter(i => i.level !== 'reference' && !i.excludeFromAI);
  const scope = mode === 'one' ? { itemIds: one ? [one] : [] } : mode === 'several' ? { itemIds: several }
    : { kinds: filters.kind ? [filters.kind] : [], statuses: filters.status ? [filters.status] : [], topics: filters.topic ? [filters.topic] : [] };
  const ready = question.trim() && (mode === 'all' || scope.itemIds.length);
  const ask = async () => {
    setBusy(true); setError(''); setResult(null);
    try { setResult(await ownerCall({ action: 'ask', question, scope })); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div>
      <div style={cardSt}>
        <Notice icon="✨" tone="ai">
          La IA lee solo lo que tienes guardado y no excluiste: tus fragmentos, el contenido extraído, tus notas y tus ideas de aplicación. No abre enlaces ni lee materiales que solo están como referencia. El contenido de tus materiales es material de consulta: si un texto trae instrucciones, no se siguen.
        </Notice>
        <span style={labelSt}>¿Sobre qué?</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {[['one', 'Un material'], ['several', 'Varios materiales'], ['all', 'Toda la biblioteca']].map(([k, l]) => <button key={k} style={chipSt(mode === k)} onClick={() => setMode(k)}>{l}</button>)}
        </div>
        {!items ? <div style={mutedSt}>Cargando…</div> : (
          <>
            {mode === 'one' && (
              <select style={inputSt} value={one} onChange={e => setOne(e.target.value)} aria-label="Material">
                <option value="">Elige un material…</option>
                {items.map(i => <option key={i.id} value={i.id} disabled={i.level === 'reference' || i.excludeFromAI}>{i.title}{i.level === 'reference' ? ' (solo referencia)' : i.excludeFromAI ? ' (excluido de la IA)' : ''}</option>)}
              </select>
            )}
            {mode === 'several' && (
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 8 }}>
                {!consultable.length && <div style={mutedSt}>Todavía no hay materiales con notas, fragmentos o contenido.</div>}
                {consultable.map(i => (
                  <label key={i.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, margin: '4px 0' }}>
                    <input type="checkbox" checked={several.includes(i.id)} onChange={() => setSeveral(s => (s.includes(i.id) ? s.filter(x => x !== i.id) : [...s, i.id]))} />
                    {i.title} <LevelBadge level={i.level} />
                  </label>
                ))}
              </div>
            )}
            {mode === 'all' && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select style={{ ...inputSt, width: 'auto' }} value={filters.kind} onChange={e => setFilters({ ...filters, kind: e.target.value })} aria-label="Tipo"><option value="">Todos los tipos</option>{LIFE_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}</select>
                <select style={{ ...inputSt, width: 'auto' }} value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} aria-label="Estado"><option value="">Todos los estados</option>{LIFE_STATUSES.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}</select>
                {topics.length > 0 && <select style={{ ...inputSt, width: 'auto' }} value={filters.topic} onChange={e => setFilters({ ...filters, topic: e.target.value })} aria-label="Tema"><option value="">Todos los temas</option>{topics.map(t => <option key={t} value={t}>{t}</option>)}</select>}
                <div style={{ ...mutedSt, width: '100%' }}>Se buscan los fragmentos más relacionados con tu pregunta (hasta {ASK_LIMITS.maxPassages}); la respuesta dice cuántos se consultaron.</div>
              </div>
            )}
          </>
        )}
        <div style={{ marginTop: 10 }}>
          <span style={labelSt}>Tu pregunta</span>
          <textarea style={{ ...textareaSt, minHeight: 70 }} value={question} maxLength={1000} placeholder="Por ejemplo: ¿qué aprendí sobre poner límites? ¿qué dice este libro sobre la identidad?" onChange={e => setQuestion(e.target.value)} />
        </div>
        <button className="action-btn btn-primary" style={{ marginTop: 8 }} disabled={busy || !ready} onClick={ask}>{busy ? 'Consultando…' : '🔎 Preguntar'}</button>
        {error && <div style={{ ...warnSt, padding: 8, marginTop: 8 }}>{error}</div>}
      </div>
      {result && <Answer r={result} />}
    </div>
  );
}
